import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import ReactHookSelect from 'react-hook-select';
import { ReactComponent as DropDownIcon } from 'Assets/svg/dropdown-icon.svg';
import CancelSaveButton from 'Components/common/CancelSaveButton';
import CheckBox from 'Components/common/CheckBoxNew';
import fieldMappingState from 'Stores/fieldMapping';
import userDetail from 'Stores/userDetail';
import integrationDataMap from './integrationDataMap';
import 'Styles/layout/_crmSettings.scss';

const renderDropDownIcon = ({ isDropDownOpen }) => {
  return <DropDownIcon className={isDropDownOpen ? 'open' : ''} />;
};

function CardWithTitle(props) {
  const { title, children } = props;
  return (
    <>
      {title && <div className="preferences-title">{title}</div>}
      <div className="preferences-card">{children}</div>
    </>
  );
}

// Essentially this component is just salesforce settings now, if needed make some adjustments
const CRMSettings = observer(props => {
  const { crmType } = useParams();
  const { redirectToIntegrations } = props;
  const [exportType, setExportType] = useState('');
  const [excludeContacts, setExcludeContacts] = useState(
    userDetail.userInfo?.teamCRMPreferences?.SALESFORCE
      ?.dont_purchase_if_present_in_crm,
  );
  const currentCRMData = integrationDataMap[crmType?.toUpperCase()];

  const hasLeads =
    currentCRMData?.sublinks?.find(data => data.linkKey === 'field-mapping')
      ?.subMenu?.length === 2 || false;

  React.useEffect(() => {
    fieldMappingState.getEnrichmentSettings(crmType.toUpperCase(), {
      notConnectedCallback: redirectToIntegrations,
    });
  }, []);

  useEffect(() => {
    setExportType(fieldMappingState.enrichmentSettingsData.exportType);
    setExcludeContacts(
      fieldMappingState.enrichmentSettingsData.dont_purchase_if_present_in_crm,
    );
  }, [fieldMappingState.enrichmentSettingsData.exportType]);

  const hasEditAccess =
    fieldMappingState.enrichmentSettingsData?.isAdmin || false;
  const crmName =
    currentCRMData?.title
      ?.split(' ')
      ?.map(data => `${data.substring(0, 1).toUpperCase()}${data.substring(1)}`)
      ?.join(' ') ?? crmType;

  const isExportTypeUpdated =
    exportType &&
    exportType !== fieldMappingState.enrichmentSettingsData.exportType;
  const isExcludeContactUpdated =
    excludeContacts !==
    fieldMappingState.enrichmentSettingsData.dont_purchase_if_present_in_crm;

  return (
    <div
      className={`integration crm-settings-wrapper${
        hasEditAccess ? '' : ' no-edit'
      }`}
    >
      {hasLeads && (
        <CardWithTitle title="General">
          <div className="setting-row-wrap">
            <div className="setting-message-wrap">
              <div className="title">Export Preference</div>
              <div className="message">
                {`When exporting to ${crmName}, export as`}
              </div>
              <div className="pref-select-wrap">
                <ReactHookSelect
                  renderDropDownIcon={renderDropDownIcon}
                  selectWrapperProps={{ className: 'salesforce-export-pref' }}
                  options={[
                    { label: 'Leads', value: 'LEAD' },
                    { label: 'Contacts', value: 'CONTACT' },
                  ]}
                  placeholder="Select"
                  value={exportType ? [exportType] : []}
                  getValue={value => {
                    setExportType(value[0]);
                  }}
                />
              </div>
            </div>
          </div>
        </CardWithTitle>
      )}
      <CardWithTitle>
        <div className="setting-row-wrap">
          <div className="setting-message-wrap">
            <div className="title">Purchase Preference</div>
            <CheckBox
              label={`When purchasing contacts, exclude contacts that are already present in Salesforce${
                exportType ? ` as a ${exportType.toLowerCase()}` : ''
              }`}
              className={`exclude-duplicate-checkbox${
                exportType ? '' : ' disabled'
              }`}
              onClick={() => {
                if (exportType) setExcludeContacts(!excludeContacts);
              }}
              isChecked={excludeContacts}
            />
          </div>
        </div>
      </CardWithTitle>
      <div className="button-wrapper">
        {(isExportTypeUpdated || isExcludeContactUpdated) && (
          <CancelSaveButton
            handleSave={async () => {
              const isSuccess = await fieldMappingState.updateEnrichmentSettings(
                crmType.toUpperCase(),
                {
                  ...(isExportTypeUpdated && { exportType }),
                  ...(isExcludeContactUpdated && {
                    dont_purchase_if_present_in_crm: excludeContacts,
                  }),
                },
              );
              if (isSuccess) {
                fieldMappingState.enrichmentSettingsData = {
                  ...fieldMappingState.enrichmentSettingsData,
                  exportType,
                  dont_purchase_if_present_in_crm: excludeContacts,
                };
                userDetail.setUserInfo({
                  ...userDetail.userInfo,
                  teamCRMPreferences: {
                    ...(userDetail.userInfo.teamCRMPreferences || {}),
                    SALESFORCE: {
                      ...(userDetail.userInfo.teamCRMPreferences?.SALESFORCE ||
                        {}),
                      export_as: exportType.toLowerCase(),
                      dont_purchase_if_present_in_crm: excludeContacts,
                    },
                  },
                });
              }
            }}
            handleCancel={() => {
              setExportType(
                fieldMappingState.enrichmentSettingsData.exportType,
              );
              setExcludeContacts(
                fieldMappingState.enrichmentSettingsData
                  .dont_purchase_if_present_in_crm,
              );
            }}
          />
        )}
      </div>
    </div>
  );
});

export default CRMSettings;
